<script setup lang="ts">
import { computed } from 'vue';

import type { FiscalShiftState } from '@/entities/fiscalization';
import type { OrderCheckoutContextEntity } from '@/entities/order';

import { fiscalizationStatusPresentation, isKktConcern } from '@/entities/fiscalization';
import { AppButton } from '@/shared/ui';

const props = defineProps<{
  canManage?: boolean;
  checkoutContext: OrderCheckoutContextEntity | null;
  fiscalShift?: FiscalShiftState | null;
  phaseMessage?: string | null;
}>();

const emit = defineEmits<{
  configure: [];
}>();

const statusPresentation = computed(() =>
  fiscalizationStatusPresentation(props.checkoutContext?.fiscalization_readiness, 'admin'),
);
/**
 * Карточка отвечает только за состояние кассы. Про смену говорит блокер расчёта
 * (`OrderShiftBlockerCard`) — иначе «Касса готова» и «Смена не открыта» снова окажутся соседями.
 */
const kktConcern = computed<FiscalShiftState | null>(() =>
  props.fiscalShift != null && isKktConcern(props.fiscalShift.status) ? props.fiscalShift : null,
);
/**
 * Говорит только устройство: серверный статус описывает клуб целиком и на планшете без
 * моста превращается во враньё («Касса готова» рядом с «Кассы на этом устройстве нет»).
 * Он остаётся единственным источником, когда `fiscalShiftState()` вернул `null` — смены
 * выключены или касса не подключена к клубу, и админу нужна кнопка «Настроить».
 */
const fallbackToServerStatus = computed(() => props.fiscalShift == null && statusPresentation.value.status !== 'ready');
const title = computed(() => kktConcern.value?.title ?? statusPresentation.value.label);
const description = computed(
  () => props.phaseMessage ?? kktConcern.value?.description ?? statusPresentation.value.description,
);
/** Исправная касса в тишине: карточку показываем, только если есть что сказать. */
const isVisible = computed(
  () =>
    props.checkoutContext?.fiscalization_required === true &&
    (kktConcern.value != null || props.phaseMessage != null || fallbackToServerStatus.value),
);
</script>

<template>
  <section
    v-if="isVisible"
    class="relative shrink-0 overflow-hidden rounded-2xl border border-(--border) bg-(--surface-sunken) p-4"
    aria-live="polite"
  >
    <div
      class="absolute inset-x-0 top-0 h-1 bg-[repeating-linear-gradient(90deg,var(--accent)_0_10px,transparent_10px_16px)] opacity-55"
    />
    <div class="flex items-start justify-between gap-3">
      <div>
        <p class="text-xs font-semibold tracking-[0.14em] text-(--text-secondary) uppercase">Касса</p>
        <p class="mt-1 text-sm font-semibold text-(--text-primary)">
          {{ title }}
        </p>
        <p class="mt-1 text-xs leading-5 text-pretty text-(--text-secondary)">
          {{ description }}
        </p>
      </div>
      <AppButton
        v-if="canManage && checkoutContext?.fiscalization_readiness?.effective_enabled !== true"
        size="sm"
        outline
        @click="emit('configure')"
      >
        Настроить
      </AppButton>
    </div>
  </section>
</template>
